import { useState } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import { Button, Col, Row, Space, Tabs, Typography } from "antd";
import { useTranslation } from "react-i18next";

import type { EmailDocumentsPageQuery } from "../../../api/graphql/queries/emailDocumentsPage/emailDocumentsPage.generated";
import DeleteEmailDocumentModal from "../BelegeView/DeleteEmailDocumentModal";
import EmailDocumentPreviewOnly from "../BelegeView/EmailDocumentPreviewOnly";
import { ReceiptChangeLog } from "./ReceiptChangeLog";
import { ReceiptCorrectionForm } from "./ReceiptCorrectionForm";
import { ReceiptMatchedTransactionSection } from "./ReceiptMatchedTransactionSection";

type ReceiptRow =
  EmailDocumentsPageQuery["emailDocumentsPage"]["items"][number];

const TAB_DATA = "data";
const TAB_MATCH = "match";

/**
 * Expanded receipt detail panel (SMT-1041): two columns —
 *  - left: the document preview;
 *  - right: a tabbed workspace with the receipt-data form ("Belegdaten", default)
 *    and the transaction matching/search ("Suche"), plus the "view logs" trigger
 *    pinned to the tab bar.
 *
 * The matching tab is lazily mounted (antd default `forceRender={false}`), so its
 * per-document query only fires once the operator opens it.
 */
export const ReceiptDetailPanel = ({
  emailDocument,
  publicId,
  email,
  onSaved,
  onDeleted,
}: {
  emailDocument: ReceiptRow;
  publicId: string;
  email?: string;
  onSaved?: () => void | Promise<unknown>;
  onDeleted?: () => void;
}) => {
  const { t } = useTranslation("ReceiptOverview");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  return (
    <>
      <Row gutter={16}>
        <Col span={12}>
          {/* No minHeight override: inherit the component default (850px) so the
            receipt is shown as large as in the old BelegeView, not cropped. */}
          <EmailDocumentPreviewOnly emailDocument={emailDocument} />
        </Col>
        <Col span={12}>
          <Tabs
            defaultActiveKey={TAB_DATA}
            tabBarExtraContent={{
              right: (
                <>
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    aria-label={t("actions.delete") as string}
                    title={t("actions.delete") as string}
                    onClick={() => setDeleteModalOpen(true)}
                    style={{ marginRight: 8 }}
                  />
                  <ReceiptChangeLog documentId={emailDocument.id} />
                </>
              ),
            }}
            items={[
              {
                key: TAB_DATA,
                label: t("detail.tabData"),
                children: (
                  <>
                    <ReceiptCorrectionForm
                      emailDocument={emailDocument}
                      publicId={publicId}
                      onSaved={onSaved}
                    />
                    {/* Read-only receipt ID shown underneath the editable data so
                        operators can copy it for investigations (SMT-992). */}
                    <Space size={4}>
                      <Typography.Text type="secondary">
                        {t("detail.receiptId")}
                      </Typography.Text>
                      <Typography.Text copyable code data-test="receiptIdValue">
                        {emailDocument.id}
                      </Typography.Text>
                    </Space>
                  </>
                ),
              },
              {
                key: TAB_MATCH,
                label: t("detail.tabMatch"),
                children: (
                  <ReceiptMatchedTransactionSection
                    email={email}
                    documentId={emailDocument.id}
                    matchedTransactionId={emailDocument.transactionId}
                    onChanged={() => onSaved?.()}
                  />
                ),
              },
            ]}
          />
        </Col>
      </Row>
      <DeleteEmailDocumentModal
        isOpen={deleteModalOpen}
        emailDocumentId={emailDocument.id}
        onClose={() => setDeleteModalOpen(false)}
        onSuccess={() => {
          setDeleteModalOpen(false);
          onDeleted?.();
        }}
      />
    </>
  );
};
